/** Net ROI Calculator page — v1 parity (permits-roi.js roicalc view). Pick one
 * of your leads to seed ARV (your override wins) / asking price, or type numbers
 * directly. The Flip / BRRRR / Margin calculator body is the shared
 * <RoiCalculator>; below it sits the itemized <RehabCalculator> (v1's rehab
 * estimator) whose checked total drives the calculator's Rehab Cost. Defaults
 * come from the org's NR_MASTER (read-only here). */
import { useMemo, useState } from "react";
import type { Lead } from "../../types";
import { natKey } from "../../lib/natkey";
import { arvOverrideOf, parseMoneyEdit, type EditsMap } from "../edits/api";
import { mergeMaster, type NrInputs } from "./math";
import { nrOverrideFor } from "./estMoney";
import { RoiCalculator } from "./RoiCalculator";
import { RehabCalculator } from "../rehab/RehabCalculator";
import type { RehabCalcState } from "../rehab/api";

/** Stand-in lead for the standalone page when nothing is picked — the rehab
 * calculator seeds from it (all-null -> blank sqft, qty 1) and its save is a
 * no-op (there is no lead to persist onto until one is picked). */
const BLANK_LEAD: Lead = {
  id: "", createdAt: 0, address: "", source: "", price: null, arv: null, arvPct: null,
  county: null, beds: null, baths: null, sqft: null, zest: null, rentZest: null, cdom: null,
  seller: null, marketStatus: null, priceDrop: null, reductions: 0, priceDrops: [],
  priceHistory: [], photos: [], desc: null, taxHistory: [], yearBuilt: null, permits: null,
  permitsChecked: false, permitUrl: null,
};

export function RoiPage({
  leads,
  edits,
  orgSettings,
  onSaveRehab,
  onSaveNrOverride,
}: {
  leads: Lead[];
  edits: EditsMap;
  orgSettings: Record<string, unknown>;
  /** Persist a picked lead's rehab calculator (same op the LeadDrawer uses). */
  onSaveRehab: (address: string, state: RehabCalcState, total: number) => void;
  /** Persist per-property ROI overrides (NR_BY). Only offered once a lead is
   *  picked — a scratch calculation has no property to save against. */
  onSaveNrOverride?: (address: string, vals: Record<string, number>) => void;
}) {
  const master = useMemo(() => mergeMaster(orgSettings["NR_MASTER"]), [orgSettings]);
  const [leadQ, setLeadQ] = useState("");
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [rehabEst, setRehabEst] = useState(0); // live total from the rehab calculator

  const picked = pickedId ? leads.find((l) => l.id === pickedId) ?? null : null;
  const pickedFields = picked ? edits.get(natKey(picked.address)) : undefined;
  // the per-property ROI override already stored for this lead, if any
  const savedOver = useMemo(
    () => (picked ? nrOverrideFor(orgSettings["NR_BY"], picked.address) : undefined),
    [picked, orgSettings],
  );

  const seeded = useMemo<Partial<NrInputs>>(() => {
    const out: Partial<NrInputs> = {};
    if (picked) {
      // v1 nrInit: arv = compedArv||listedArv||p.arv; purchase = offer||price
      out.arv = arvOverrideOf(pickedFields) ?? picked.arv ?? 0;
      out.purchase = parseMoneyEdit(pickedFields?.["offerPrice"]) ?? picked.price ?? 0;
    }
    // The rehab estimator drives Rehab Cost, but only when it has a real total —
    // an empty sheet ($0) never blanks a manually typed Rehab Cost (v1 rule).
    if (rehabEst > 0) out.rehab = rehabEst;
    return out;
  }, [picked, pickedFields, rehabEst]);

  const hits = useMemo(() => {
    const needle = leadQ.trim().toLowerCase();
    if (!needle) return [];
    return leads.filter((l) => l.address.toLowerCase().includes(needle)).slice(0, 6);
  }, [leads, leadQ]);

  return (
    <div className="roipage">
      <header className="bar">
        <div className="leadpick">
          <input
            placeholder={picked ? picked.address : "Seed from a lead — search address…"}
            value={leadQ}
            onChange={(e) => setLeadQ(e.target.value)}
          />
          {hits.length ? (
            <div className="pickmenu">
              {hits.map((l) => (
                <button
                  key={l.id}
                  onClick={() => {
                    setPickedId(l.id);
                    setLeadQ("");
                  }}
                >
                  {l.address}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {picked ? (
          <button className="vtab" onClick={() => setPickedId(null)}>clear lead</button>
        ) : null}
        <span className="count">defaults from your ROI settings{orgSettings["NR_MASTER"] ? "" : " (built-in)"}</span>
      </header>

      <RoiCalculator
        key={pickedId ?? "none"}
        master={master}
        seed={seeded}
        {...(picked && onSaveNrOverride ? { address: picked.address, onSaveOver: onSaveNrOverride } : {})}
        {...(savedOver ? { savedOver } : {})}
      />

      <RehabCalculator
        key={"rehab-" + (pickedId ?? "none")}
        lead={picked ?? BLANK_LEAD}
        fields={pickedFields}
        onSave={(state, total) => { if (picked) onSaveRehab(picked.address, state, total); }}
        onTotal={setRehabEst}
        startOpen
      />
    </div>
  );
}
